import {
  Button,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';

import { AiTwotoneEdit } from 'react-icons/ai';
import { BiAddToQueue } from 'react-icons/bi';
import React, { useContext, useEffect, useState } from 'react';
import PageTitle from '../../../components/page-title/PageTitle';
import { useTypeDocumentService } from '../../../hooks/services/useTypeDocumentService';
import { useToastrVanila } from '../../../hooks/util/useToastrVanila';
import IPage from '../../../interfaces/IPage';
import ITypeDocument from '../../../interfaces/ITypeDocument';
import { Container } from './DocumentTypeList.styled';
import { useNavigate } from 'react-router-dom';

import CustomTablePagination from '../../../components/custom-table-pagination/CustomTablePagination';
import CustomTableSortLabel from '../../../components/custom-table-sort-label/CustomTableSortLabel';
import { BsTrash } from 'react-icons/bs';
import { hasAccessToAction } from '../../../util/hasAccessTo';
import UserContext from '../../../context/user/UserContext';
import DescriptionIcon from '@mui/icons-material/Description';

interface DocumentTypeListProps extends IPage {}

const DocumentTypeList = (props: DocumentTypeListProps) => {
  window.document.title = props.title;
  const [documentTypes, setDocumentTypes] = useState<ITypeDocument[] | null>(null);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [orderDirection, setOrderDirection] = useState<'asc' | 'desc'>('asc');
  const [orderField, setOrderField] = useState<string>('description');
  const [loading, setLoading] = useState(false);

  const request = useTypeDocumentService();
  const toastr = useToastrVanila();
  const navigate = useNavigate();

  const { state } = useContext(UserContext);
  const { resourcesWithActions } = state;

  const isEdit = hasAccessToAction(
    ["tipo-documento-atestado-web"],
    resourcesWithActions,
    "Editar"
  );

  const listDocumentTypes = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    orderDirection: 'asc' | 'desc',
  ) => {
    setLoading(true);
    await request
      .list(perPage, currentPage + 1, orderField, orderDirection)
      .then(result => {
        setDocumentTypes([...result?.data]);
        setTotalRows(result.totalRows);
        setLoading(false);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  const removeDocumentType = async (id: number) => {
    setLoading(true);
      await request
      .remove(id)
      .then(async result => {
        await listDocumentTypes(perPage, currentPage, orderField, orderDirection);
        setLoading(false);
      })
      .catch(error => {
        toastr.error(error.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    listDocumentTypes(perPage, currentPage, orderField, orderDirection);
  }, []);

  return (
    <Container>
      <PageTitle
        title="LISTAGEM DE TIPOS DE DOCUMENTO"
        icon={<DescriptionIcon sx={{ color: 'primary.main' }} />}
      >
        <Tooltip title="Cadastrar novo" placement="left-start">
          <Button
            variant="outlined"
            size="small"
            endIcon={<BiAddToQueue />}
            onClick={() => navigate(`/tipo-documento/${0}/new`)}
            disabled={!isEdit}
          >
            Adicionar tipo de documento
          </Button>
        </Tooltip>
      </PageTitle>

      <TableContainer component={Paper} sx={{ marginTop: '20px' }}>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="id"
                label="Código"
                handleRequest={listDocumentTypes}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="description"
                label="Descrição"
                handleRequest={listDocumentTypes}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="isActive"
                label="Ativo"
                handleRequest={listDocumentTypes}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="order"
                label="Ordem de exibição"
                handleRequest={listDocumentTypes}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="cidRequired"
                label="CID Obrigatória"
                handleRequest={listDocumentTypes}
                direction={orderDirection}
                setDirection={setOrderDirection}
                setField={setOrderField}
              />

              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="crmRequired"
                label="CRM obrigatório"
                handleRequest={listDocumentTypes}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <CustomTableSortLabel
                perPage={perPage}
                currentPage={currentPage}
                orderField={orderField}
                field="hospitalRequired"
                label="Hospital Obrigatório"
                handleRequest={listDocumentTypes}
                direction={orderDirection}
                setField={setOrderField}
                setDirection={setOrderDirection}
              />
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {documentTypes &&
              documentTypes.map(document => {
                return (
                  <TableRow key={document.id} hover={true}>
                    <TableCell>{document.id}</TableCell>
                    <TableCell>{document.description}</TableCell>
                    <TableCell>{document.isActive ? 'Sim' : 'Não'}</TableCell>
                    <TableCell>{document.order}</TableCell>
                    <TableCell>{document.cidRequired ? 'Sim' : 'Não'}</TableCell>
                    <TableCell>{document.crmRequired ? 'Sim' : 'Não'}</TableCell>
                    <TableCell>{document.hospitalRequired ? 'Sim' : 'Não'}</TableCell>
                    <TableCell>
                      <Tooltip title="Editar" placement="top">
                        <IconButton onClick={() => navigate(`/tipo-documento/${document.id}/edit`)}>
                          <AiTwotoneEdit style={{ fontSize: '15px' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir" placement="top">
                        <IconButton onClick={() => removeDocumentType(document.id || 0)} disabled={!isEdit}>
                          <BsTrash style={{ fontSize: '15px' }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        totalRows={totalRows}
        currentPage={currentPage}
        perPage={perPage}
        handleRequest={listDocumentTypes}
        setPerPage={setPerPage}
        setCurrentPage={setCurrentPage}
        orderField={orderField}
        orderDirection={orderDirection}
      />
    </Container>
  );
};

export default DocumentTypeList;
